/**
 * Shared left-rail component for every step of the booking wizard.
 * Mirrors the mockup `.rail` region:
 *   - Step list (Service → Method → Details → Documents → Your Details)
 *   - "Summary" card accumulated from prior answers.
 */

import type { BookingWizardState, WizardStepIndex } from '../types';
import { WIZARD_STEP_LABELS } from '../types';

export interface BookingRailProps {
  currentStep: WizardStepIndex;
  state: BookingWizardState;
}

export function BookingRail({ currentStep, state }: BookingRailProps): JSX.Element {
  return (
    <aside className="rae-wizard__rail" aria-label="Booking progress">
      <h4>Book Appointment</h4>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {WIZARD_STEP_LABELS.map((label, i) => {
          const isDone = i < currentStep;
          const isActive = i === currentStep;
          return (
            <li
              key={label}
              className={`rae-wizard__step ${isDone ? 'is-done' : ''} ${isActive ? 'is-active' : ''}`.trim()}
              aria-current={isActive ? 'step' : undefined}
            >
              <span className="rae-wizard__step-num" aria-hidden>
                {isDone ? '✓' : i + 1}
              </span>
              <span className="rae-wizard__step-label">{label}</span>
              {i < WIZARD_STEP_LABELS.length - 1 && <span className="rae-wizard__step-line" aria-hidden />}
            </li>
          );
        })}
      </ol>

      {currentStep >= 1 && (
        <div className="rae-wizard__summary">
          {state.service && (
            <>
              <h5>Service</h5>
              <div className="val">{state.service.title}</div>
              <div className="sub">{state.service.durationMin} minutes</div>
            </>
          )}
          {state.method && (
            <>
              <h5>Meeting method</h5>
              <div className="val">{methodLabel(state.method)}</div>
              <div className="sub">{methodSub(state.method)}</div>
            </>
          )}
          {state.branch && (
            <>
              <h5>Branch</h5>
              <div className="val">{state.branch.branchName}</div>
            </>
          )}
          {state.date && state.time && (
            <>
              <h5>When</h5>
              <div className="val">{formatDateShort(state.date)}</div>
              <div className="sub">{formatTime12(state.time)} (CDT)</div>
            </>
          )}
          {state.files.length > 0 && currentStep >= 4 && (
            <>
              <h5>Documents</h5>
              <div className="val">{state.files.length} file{state.files.length === 1 ? '' : 's'}</div>
            </>
          )}
        </div>
      )}
    </aside>
  );
}

function methodLabel(m: BookingWizardState['method']): string {
  if (m === 'inPerson') return 'In Person';
  if (m === 'video') return 'Video';
  if (m === 'phone') return 'Phone';
  return '';
}
function methodSub(m: BookingWizardState['method']): string {
  if (m === 'inPerson') return 'Branch location';
  if (m === 'video') return 'Microsoft Teams link';
  if (m === 'phone') return 'We call you';
  return '';
}

function formatDateShort(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime12(hhmm: string): string {
  const parts = hhmm.split(':');
  const h24Str = parts[0] ?? '0';
  const mStr = parts[1] ?? '00';
  const h24 = Number.parseInt(h24Str, 10);
  const h12 = ((h24 + 11) % 12) + 1;
  const ampm = h24 >= 12 ? 'PM' : 'AM';
  return `${h12}:${mStr} ${ampm}`;
}
